import React, {
  lazy, Suspense, useEffect, useState,
} from 'react';
import PropTypes from 'prop-types';
import Title from '../../components/title';
import {
  MainContainer,
  Container,
  CanvasContainer,
  Canvas,
  ImageLogo,
  ContentContainer,
  Separator,
  ScrollContainer,
  ScrollIconContainer,
  ScrollIcon,
  Text,
  ContentAminContainer,
} from './style';
import PresentationMobile from './mobile';

const CanvasLogo = lazy(() => import('../../components/canvasLogo'));
const DEFER_3D_TIMEOUT_MS = 1800;
const INTERACTION_EVENTS = ['pointerdown', 'touchstart', 'keydown', 'scroll'];

const strings = {
  scroll: 'scroll',
};
function Presentation({
  children,
  onEnd,
  contentScroll,
  planContainer,
  setShowContent,
  showContent,
  setHidden,
  hidden,
}) {
  const smallMobileDevices = window.matchMedia('(max-width: 960px)');
  const [enable3D, setEnable3D] = useState(false);

  useEffect(() => {
    let idleCallbackId;
    let timeoutId;

    const enableHero3D = () => {
      setEnable3D(true);
      INTERACTION_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, enableHero3D);
      });
    };

    if ('requestIdleCallback' in window) {
      idleCallbackId = window.requestIdleCallback(enableHero3D, {
        timeout: DEFER_3D_TIMEOUT_MS,
      });
    } else {
      timeoutId = window.setTimeout(enableHero3D, DEFER_3D_TIMEOUT_MS);
    }

    INTERACTION_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, enableHero3D, { passive: true });
    });

    return () => {
      INTERACTION_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, enableHero3D);
      });
      if (idleCallbackId) {
        window.cancelIdleCallback(idleCallbackId);
      }
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
    };
  }, []);

  useEffect(() => {
    if (smallMobileDevices.matches) return undefined;
    const onContainerScroll = () => {
      const {
        offsetHeight,
        scrollTop,
      } = contentScroll.current;

      if ((offsetHeight + scrollTop) + 50 >= planContainer.current.clientHeight) {
        onEnd(true);
        setHidden(true);
      } else {
        setHidden(false);
        onEnd(false);
      }
    };

    const onWheel = (e) => {
      if (window.innerWidth < 960) return;

      if (!showContent && e.deltaY > 0) {
        setShowContent(true);
      } else if (contentScroll.current.scrollTop === 0) {
        setShowContent(false);
        onEnd(false);
      }
    };

    contentScroll.current.addEventListener('scroll', onContainerScroll);
    document.addEventListener('wheel', onWheel);

    return () => {
      contentScroll.current?.removeEventListener('scroll', onContainerScroll);
      document.removeEventListener('wheel', onWheel);
    };
  }, []);

  if (window.innerWidth < 960) {
    return (<PresentationMobile contentScroll={contentScroll}>{children}</PresentationMobile>);
  }

  const handleAnimation = () => {
    if (hidden) return 'hidden';
    if (showContent) return 'reduce' || 'as';
    return 'expand';
  };

  return (
    <MainContainer id="maincontainer">
      <Container animate={handleAnimation()} hiddenContainer={hidden}>
        <CanvasContainer animate={showContent ? 'reduce' : 'expand'}>
          {!showContent && (
            <>
              <Canvas>
                {enable3D ? (
                  <Suspense fallback={<ImageLogo forceVisible />}>
                    <CanvasLogo />
                  </Suspense>
                ) : (
                  <ImageLogo forceVisible />
                )}
              </Canvas>
              <ImageLogo />
            </>
          )}
        </CanvasContainer>
        <Title reduce={showContent} hidden={hidden} />
      </Container>
      <ContentContainer animate={showContent ? 'expand' : 'reduce'} hidden={hidden}>
        <Separator hiddenSeparator={hidden} />
        <ScrollContainer animate={showContent ? 'hidde' : 'show'}>
          <ScrollIconContainer>
            <ScrollIcon />
          </ScrollIconContainer>
          <Text>{strings.scroll}</Text>
        </ScrollContainer>
        <ContentAminContainer
          ref={contentScroll}
          animate={showContent ? 'show' : 'hidde'}
          id="scroll-container"
          hiddenSeparator={hidden}
        >
          {children}
        </ContentAminContainer>
      </ContentContainer>
    </MainContainer>
  );
}

Presentation.defaultProps = {
  children: null,
  onEnd: () => null,
  contentScroll: null,
  planContainer: null,
  showContent: false,
  hidden: false,
  setShowContent: () => null,
  setHidden: () => null,
};

Presentation.propTypes = {
  children: PropTypes.node,
  onEnd: PropTypes.func,
  setShowContent: PropTypes.func,
  setHidden: PropTypes.func,
  showContent: PropTypes.bool,
  hidden: PropTypes.bool,
  // eslint-disable-next-line react/forbid-prop-types
  contentScroll: PropTypes.any,
  planContainer: PropTypes.any,
};

export default Presentation;
